import { PortfolioService } from '@amzn/awscat-portfolio-assessment-service-client';
import { BreadcrumbGroup, BreadcrumbGroupProps } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useHistory, useLocation, useRouteMatch } from 'react-router';

import { constants } from '../../common/Localization';
import { MatchParam } from '../../common/types';
import { PortfolioContext } from '../../contexts/PortfolioContext';

type Props = {isAuthenticated: boolean};

const portfolioService = new PortfolioService();

function Breadcrumbs({ isAuthenticated }: Props): JSX.Element {
    const history = useHistory();
    const location = useLocation();
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId;

    const tcoMatch: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const tcoAssessmentId = tcoMatch?.params?.tcoAssessmentId ?? 'default';

    const adjustmentEditMatch: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId/adjustments/edit/adjustmentId',
    });
    const adjustmentId = adjustmentEditMatch?.params?.adjustmentId;

    const [ items, setItems ] = useState<BreadcrumbGroupProps.Item[]>([]);
    const [ portfolioName, setPortfolioName ] = useState<string | null>(null);

    const { portfolio } = useContext(PortfolioContext);

    const generateBreadcrumbItems = (): void => {
        const updatedItems: BreadcrumbGroupProps.Item[] = [];

        const locationParts: string[] = location.pathname.split('/');

        if (location.pathname === '/') {
            setItems(updatedItems);
            return;
        }

        updatedItems.push(
            {text: constants.BREADCRUMB_ALL_PORTFOLIOS, href: '/portfolios'},
        );

        if (portfolioId) {
            updatedItems.push(
                {text: portfolioName ?? constants.BREADCRUMB_CURRENT_PORTFOLIO,
                    href: `/portfolio/${portfolioId}/management/assets/servers`},
            );
        }
        if (locationParts.includes('management')) {
            if (locationParts.includes('dashboard')) {
                updatedItems.push(
                    {text: constants.BREADCRUMB_DASHBOARDS, href: `/portfolio/${portfolioId}/management/dashboard`},
                );
            }
            else if (locationParts.includes('import-from-file')) {
                updatedItems.push(
                    {text: constants.BREADCRUMB_IMPORT_FILE, href: `/portfolio/${portfolioId}/management/import-from-file`},
                );
            }
            else if (locationParts.includes('import-from-ads')) {
                updatedItems.push(
                    {text: constants.BREADCRUMB_IMPORT_FROM_ADS, href: `/portfolio/${portfolioId}/management/import-from-ads`},
                );
            }
            else if (locationParts.includes('contact-management')) {
                updatedItems.push(
                    {text: constants.BREADCRUMB_CONTACT_MANAGEMENT, href:`/portfolio/${portfolioId}/management/contact-management`},
                );
            }
            else if (locationParts.includes('data-collection')) {
                updatedItems.push(
                    {text: constants.BREADCRUMB_DATA_COLLECTION, href:`/portfolio/${portfolioId}/management/data-collection`},
                );
            }
            else if (locationParts.includes('dependencies')) {
                updatedItems.push(
                    {text: constants.BREADCRUMB_MANAGE_DEPENDENCIES, href: `/portfolio/${portfolioId}/management/dependencies/application-servers`},
                );
            }
            else if (locationParts.includes('rules')) {
                if (locationParts.includes('create')) {
                    updatedItems.push(
                        {text: 'Create rule', href: `/portfolio/${portfolioId}/management/assets/rules/create`}
                    );
                } else {
                    updatedItems.push(
                        {text: 'Edit rule', href: `/portfolio/${portfolioId}/management/assets/rules/edit`}
                    );
                }
            }
            else {
                updatedItems.push(
                    {text: constants.BREADCRUMB_MANAGE_ASSETS, href: `/portfolio/${portfolioId}/management/assets/servers`},
                );

                if (locationParts.includes('application-detail')) {
                    if (locationParts.includes('overview')) {
                        updatedItems.push({text: 'Application Overview', href: `/portfolio/${portfolioId}/management/application-detail/overview`});
                    } else if (locationParts.includes('resources')) {
                        updatedItems.push({text: 'Application Resources', href: `/portfolio/${portfolioId}/management/application-detail/resources`});
                    } else if (locationParts.includes('cost')) {
                        updatedItems.push(
                            {text: 'Application Cost Detail', href: `/portfolio/${portfolioId}/management/application-detail/cost`}
                        );
                    }
                }
            }
        }
        else if (locationParts.includes('tco')) {
            if (locationParts.includes('migration-strategy')) {
                updatedItems.push(
                    {text: constants.BREADCRUMB_MIGRATION_STRATEGY_RECOMMENDATION, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/migration-strategy`},
                );
            }
            else if (locationParts.includes('migration')) {
                updatedItems.push(
                    {text: constants.BREADCRUMB_MIGRATION_COST, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/migration/summary`},
                );
                if (locationParts.includes('cash-flow')) {
                    updatedItems.push(
                        {text: constants.BREADCRUMB_MIGRATION_CASH_FLOW, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/migration/cash-flow`},
                    );
                }
            }
            else {
                updatedItems.push(
                    {text: constants.BREADCRUMB_TCO, href:`/portfolio/${portfolioId}/tco/${tcoAssessmentId}/summary`},
                );
                if (locationParts.includes('aws')) {
                    if (locationParts.includes('server-recommendations')) {
                        updatedItems.push({
                            text: constants.BREADCRUMB_AWS_EC2_RECOMMENDATIONS, 
                            href:`/portfolio/${portfolioId}/tco/${tcoAssessmentId}/aws/server-recommendations`
                        });
                    }
                    else if (locationParts.includes('database-recommendations')) {
                        updatedItems.push({
                            text: constants.BREADCRUMB_AWS_RDS_RECOMMENDATIONS, 
                            href:`/portfolio/${portfolioId}/tco/${tcoAssessmentId}/aws/database-recommendations`
                        });
                    }
                    else if (locationParts.includes('server-costs')) {
                        updatedItems.push(
                            {text: constants.BREADCRUMB_AWS_EC2_COSTS, href:`/portfolio/${portfolioId}/tco/${tcoAssessmentId}/aws/server-costs`},
                        );
                    }
                    else if (locationParts.includes('database-costs')) {
                        updatedItems.push(
                            {text: constants.BREADCRUMB_AWS_RDS_COSTS, href:`/portfolio/${portfolioId}/tco/${tcoAssessmentId}/aws/database-costs`},
                        );
                    }
                    else if (locationParts.includes('storage-network-costs')) {
                        updatedItems.push({
                            text: constants.BREADCRUMB_AWS_STORAGE_NETWORK_COSTS, 
                            href:`/portfolio/${portfolioId}/tco/${tcoAssessmentId}/aws/storage-network-costs`
                        });
                    }
                    else if (locationParts.includes('it-labor-support-costs')) {
                        updatedItems.push({
                            text: constants.BREADCRUMB_AWS_IT_SUPPORT_COSTS, 
                            href:`/portfolio/${portfolioId}/tco/${tcoAssessmentId}/aws/it-labor-support-costs`
                        });
                    }
                }
                else if (locationParts.includes('on-prem')) {
                    if (locationParts.includes('server-costs')) {
                        updatedItems.push(
                            {text: constants.BREADCRUMB_ON_PREM_SERVER_COST, href:`/portfolio/${portfolioId}/tco/${tcoAssessmentId}/on-prem/server-costs`},
                        );
                    }
                    else if (locationParts.includes('database-costs')) {
                        updatedItems.push(
                            {text: constants.BREADCRUMB_ON_PREM_DATABASE_COST, href:`/portfolio/${portfolioId}/tco/${tcoAssessmentId}/on-prem/database-costs`},
                        );
                    }
                    else if (locationParts.includes('storage-network-costs')) {
                        updatedItems.push(
                            {text: constants.BREADCRUMB_ON_PREM_STORAGE_NETWORK_COST, href:`/portfolio/${portfolioId}/tco/on-prem/storage-network-costs`},
                        );
                    }
                    else if (locationParts.includes('it-labor-costs')) {
                        updatedItems.push(
                            {text: constants.BREADCRUMB_ON_PREM_IT_LABOR_COST, href:`/portfolio/${portfolioId}/tco/${tcoAssessmentId}/on-prem/it-labor-costs`},
                        );
                    }
                }
                else if (locationParts.includes('adjustments')) {
                    updatedItems.push(
                        {text: constants.BREADCRUMB_ADJUSTMENTS, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/adjustments`},
                    );
                    if (locationParts.includes('add')) {
                        updatedItems.push(
                            {text: constants.BREADCRUMB_ADD_ADJUSTMENT, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/adjustments/add`},
                        );
                    } else if (locationParts.includes('edit')) {
                        updatedItems.push({
                            text: constants.BREADCRUMB_EDIT_ADJUSTMENT,
                            href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/adjustments/edit/${adjustmentId}`},
                        );
                    }
                }
                else if (locationParts.includes('sql-server-optimization')) {
                    updatedItems.push(
                        {text: constants.BREADCRUMB_SQL_SERVER_OPTIMIZATION, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/sql-server-optimization`},
                    );
                }
                else if (locationParts.includes('mainframe-summary')) {
                    updatedItems.push(
                        {text: constants.BREADCRUMB_MAINFRAME_SUMMARY, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/mainframe-summary`},
                    );
                }
                else if (locationParts.includes('application-summary')) {
                    updatedItems.push(
                        {text: constants.BREADCRUMB_APPLICATION_COST_SUMMARY, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/application-summary`},
                    );
                }
            }
        }
        else if (locationParts.includes('planning')) {
            updatedItems.push(
                {text: constants.BREADCRUMB_PLAN, href: `/portfolio/${portfolioId}/planning/grouping`},
            );
            if (locationParts.includes('prioritization')) {
                updatedItems.push(
                    {text: constants.BREADCRUMB_APPLICATION_PRIORITIZATION, href: `portfolio/${portfolioId}/planning/prioritization`},
                );
            }
            else if (locationParts.includes('grouping')) {
                updatedItems.push(
                    {text: constants.BREADCRUMB_DEPENDENCY_GROUPS, href: `portfolio/${portfolioId}/planning/grouping`},
                );
            }
            else if (locationParts.includes('waves')) {
                updatedItems.push(
                    {text: constants.BREADCRUMB_MIGRATION_WAVES, href: `portfolio/${portfolioId}/planning/waves`},
                );
            }
        }
        else if (locationParts.includes('properties')) {
            updatedItems.push(
                {text: constants.BREADCRUMB_PROPERTIES, href: `/portfolio/${portfolioId}/properties`},
            );
        }
        else if (locationParts.includes('new-portfolio')) {
            updatedItems.push(
                {text: constants.BREADCRUMB_NEW_PORTFOLIO, href: '/new-portfolio'},
            );
        }
        else if (locationParts.includes('faq')) {
            updatedItems.push(
                {text: constants.BREADCRUMB_FAQ, href: '/faq'},
            );
        }
        setItems(updatedItems);
    };

    useEffect(() => {
        const getPortfolioNameAsync = async () => {
            try {
                if (portfolioId && isAuthenticated) {
                    const portfolio = await portfolioService.get(portfolioId);
                    const retrievedPortfolioName = portfolio.name;
                    setPortfolioName(retrievedPortfolioName);
                }
            } catch (err) {
                setPortfolioName(null);
                console.log(err);
            }
        };
        getPortfolioNameAsync();
    }, [ portfolioId, isAuthenticated, portfolio.name ]);

    useEffect(() => {
        generateBreadcrumbItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ portfolioName, location.pathname ]);

    return (
        <BreadcrumbGroup
            items={items}
            ariaLabel='Breadcrumbs'
            onFollow={event => {
                event.preventDefault();
                history.push(event.detail.href);
            }}
        />
    );
};

export default Breadcrumbs;
